import { useEffect, useState } from 'react';

/** Tailwind's `md`. One number, one place. */
const DESKTOP_MIN = 768;

/* ===========================================================================
 * IS THIS A PHONE? — asked once, answered in JS rather than in CSS.
 *
 * The obvious way to make a table survive a phone is to render both shapes and
 * let `md:hidden` / `hidden md:block` pick one. `ResponsiveTable` did exactly
 * that, and it is wrong at scale for two reasons that only appear once EVERY
 * table does it:
 *
 *   • every cell is then in the document twice. On a 500-row roster that is a
 *     thousand renderings of a `PersonCell`, each with its own authenticated
 *     photo request, for five hundred rows nobody will see.
 *   • both are offered to a screen reader unless the stylesheet is present and
 *     doing the hiding — which is exactly the assumption a test does not make.
 *
 * Measuring instead renders ONE. The table is a table on a desktop and a list
 * of cards on a phone, and the other shape does not exist.
 *
 * WHY `innerWidth` AND NOT `matchMedia`. `matchMedia` is the better API and the
 * wrong one here: jsdom implements it as a stub that answers "no match" to
 * every query, so a `(min-width: 768px)` question comes back false and every
 * table in the test suite renders as a phone. There is no way to tell that
 * stub's "no" from a real narrow window. `innerWidth` is a number both
 * environments report honestly — jsdom's is 1024 — so the same code path is
 * exercised in a test as in a browser, which is the point of asking at all.
 * ======================================================================== */
export function useIsCompact(): boolean {
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < DESKTOP_MIN
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = (): void => setCompact(window.innerWidth < DESKTOP_MIN);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return compact;
}
